import { Fragment } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

const buildTeamMemberAlt = ({ title, name, description }) =>
  ['團隊成員', title, name, description].filter(Boolean).join('-')

const getMemberGridPosition = (index, rowOffset) => ({
  gridColumn: (index % 2) + 1,
  gridRow: Math.floor(index / 2) * 5 + rowOffset,
})

const getDesktopMemberGridPosition = (index, rowOffset) => (theme) => ({
  [theme.breakpoints.up('md')]: {
    gridColumn: (index % 4) + 1,
    gridRow: Math.floor(index / 4) * 5 + rowOffset,
  },
})

const isLastMemberRow = (index, memberCount, columnCount) =>
  Math.floor(index / columnCount) ===
  Math.floor((memberCount - 1) / columnCount)

const TeamMembers = ({ members }) => {
  const teamMembers = members?.slice(0, 8) || []

  if (!teamMembers.length) return null

  return (
    <Box component="section" mb={{ xs: 12, md: 18 }}>
      <Typography component="h2" variant="h5" mb={6}>
        團隊成員
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        }}
        columnGap={{ xs: 4, sm: 6, xl: 8 }}
        textAlign="center"
      >
        {teamMembers.map((member, index) => (
          <Fragment key={member._id || index}>
            <Box
              {...getMemberGridPosition(index, 1)}
              minWidth={0}
              p={3}
              sx={[
                { aspectRatio: '1 / 1' },
                getDesktopMemberGridPosition(index, 1),
              ]}
            >
              {!!member?.image?.url && (
                <Box
                  position="relative"
                  width="100%"
                  height="100%"
                  borderRadius="21px"
                  overflow="hidden"
                >
                  <Image
                    src={member.image.url}
                    alt={buildTeamMemberAlt(member)}
                    fill
                    sizes="(max-width: 767px) 50vw, (max-width: 1600px) 25vw, 368px"
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
              )}
            </Box>

            <Typography
              {...getMemberGridPosition(index, 2)}
              minWidth={0}
              minHeight="1.3em"
              variant="labelLarge"
              color="text.minus1"
              sx={[
                { overflowWrap: 'anywhere', wordBreak: 'break-word' },
                getDesktopMemberGridPosition(index, 2),
              ]}
            >
              {member.title ? member.title.slice(0, 10) : null}
            </Typography>
            <Box
              {...getMemberGridPosition(index, 3)}
              minWidth={0}
              alignSelf="start"
              mt={1}
              sx={getDesktopMemberGridPosition(index, 3)}
            >
              <Typography
                variant="titleMedium"
                color="text.minus1"
                lineHeight={1.2}
                letterSpacing={0}
                display="-webkit-box"
                overflow="hidden"
                sx={{
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {member.name.slice(0, 16)}
              </Typography>
            </Box>
            <Box
              {...getMemberGridPosition(index, 4)}
              minWidth={0}
              mt={member.description ? 2 : 0}
              sx={getDesktopMemberGridPosition(index, 4)}
            >
              {!!member.description && (
                <Typography
                  variant="paraBodySmall"
                  color="text.secondary"
                  lineHeight={1.4}
                  letterSpacing={0}
                  sx={{
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {member.description.slice(0, 50)}
                </Typography>
              )}
            </Box>
            <Box
              {...getMemberGridPosition(index, 5)}
              minWidth={0}
              height={{
                xs: isLastMemberRow(index, teamMembers.length, 2) ? 0 : '24px',
                sm: isLastMemberRow(index, teamMembers.length, 2) ? 0 : '32px',
                md: isLastMemberRow(index, teamMembers.length, 4) ? 0 : '32px',
              }}
              sx={getDesktopMemberGridPosition(index, 5)}
            />
          </Fragment>
        ))}
      </Box>
    </Box>
  )
}

export default TeamMembers
